import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    GetAccountSnapshotByRelationshipResponse,
    GetAccountSnapshotResponse
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    Color,
    ErrorMessage,
    forLifeOf,
    getDynamicRouteLink,
    LifecycleAware
} from '@lsq-frontend/common-ui';
import {
    ClaimService,
    getRouterLinkUrlById,
    Icons,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { startOfMonth } from 'date-fns';
import { Observable } from 'rxjs';
import {
    atlas,
    CUSTOMER_COLLECTIONS,
    CUSTOMER_INELIGIBLES,
    CUSTOMER_PURCHASES,
    REPORTS_AGING,
    REPORTS_AVAILABLE_FUNDS_SUMMARY,
    REPORTS_COLLECTIONS,
    REPORTS_INELIGIBLES,
    REPORTS_PURCHASES
} from '@lsq-frontend/fasttrack-shared/routing-atlas';

@LifecycleAware()
@Component({
    selector: 'summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SummaryComponent implements OnInit, OnDestroy {
    @Input() public accountSnapshotDataSource: LsqDataSource<
        GetAccountSnapshotResponse & GetAccountSnapshotByRelationshipResponse
    >;
    @Input() public debtorKey: string;

    public accountSnapshotData$: Observable<
        GetAccountSnapshotResponse & GetAccountSnapshotByRelationshipResponse
    >;
    public invoiceCount: number; // Needed for i18n.

    public startOfMonth = startOfMonth;
    public today = new Date();
    public isMobile = false;
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly dataSourceErrorCopy = ErrorMessage.dataSourceError;
    public readonly agingRouterLinkUrl = getRouterLinkUrlById(atlas, REPORTS_AGING);
    public readonly purchasesRouterLinkUrl = getRouterLinkUrlById(atlas, REPORTS_PURCHASES);
    public readonly purchasesByCustomerRouterLinkUrl = getRouterLinkUrlById(
        atlas,
        CUSTOMER_PURCHASES
    );
    public readonly collectionsRouterLinkUrl = getRouterLinkUrlById(atlas, REPORTS_COLLECTIONS);
    public readonly collectionsByCustomerRouterLinkUrl = getRouterLinkUrlById(
        atlas,
        CUSTOMER_COLLECTIONS
    );
    public readonly ineligiblesRouterLinkUrl = getRouterLinkUrlById(atlas, REPORTS_INELIGIBLES);
    public readonly ineligiblesByCustomerRouterLinkUrl = getRouterLinkUrlById(
        atlas,
        CUSTOMER_INELIGIBLES
    );
    public readonly availableFundsSummaryRouterLinkUrl = getRouterLinkUrlById(
        atlas,
        REPORTS_AVAILABLE_FUNDS_SUMMARY
    );

    constructor(
        private _responsiveService: ResponsiveService,
        private _router: Router,
        private _claimService: ClaimService
    ) {}

    public ngOnInit(): void {
        this._responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isMobile) => {
            this.isMobile = isMobile;
        });

        if (this.accountSnapshotDataSource) {
            this.accountSnapshotData$ = this.accountSnapshotDataSource.connect();
        }
    }

    public ngOnDestroy(): void {
        if (this.accountSnapshotDataSource) {
            this.accountSnapshotDataSource.disconnect();
        }
    }

    public getCustomerLink(link: string): string {
        return getDynamicRouteLink(link, { debtorKey: this.debtorKey });
    }

    public goToLinkOnMobile(dest: string, queryParams = {}): void {
        const canViewReports = this._claimService.isOnSync(DashboardClaim.ViewReports);
        if (canViewReports) {
            if (this.isMobile) {
                // Using 'navigate' because 'navigateByUrl' strips the queryParams if they
                // aren't in the link string as well
                this._router.navigate([dest], { queryParams });
            }
        }
    }
}
